import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Wrench, AlertTriangle, Plus, BarChart2, History, Pencil, Trash2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api/api';
import HourometerModal from '../components/HourometerModal';
import EquipmentModal from '../components/EquipmentModal';
import DeleteEquipmentModal from '../components/DeleteEquipmentModal';
import { useAuth } from '../context/AuthContext';

const STATUS_CLASSES = {
  operative: 'badge-operative', alert: 'badge-alert',
  breakdown: 'badge-breakdown', inactive: 'badge-inactive'
};
const STATUS_LABELS = { operative: 'Operativo', alert: 'Alerta', breakdown: 'Avería', inactive: 'Inactivo' };

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState(null);
  const [records, setRecords] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('horometro');
  const [showHourometer, setShowHourometer] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());

  const load = () => {
    Promise.all([
      api.get(`/equipment/${id}`),
      api.get(`/hourometer?equipment_id=${id}&year=${year}`),
      api.get(`/work-orders?equipment_id=${id}`),
      api.get(`/equipment/${id}/activity`),
    ]).then(([eqRes, recRes, woRes, actRes]) => {
      setEquipment(eqRes.data);
      setRecords(recRes.data);
      setWorkOrders(woRes.data);
      setActivityLog(actRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, year]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
    </div>
  );

  if (!equipment) return (
    <div className="p-8 text-center text-gray-500">Equipo no encontrado</div>
  );

  const chartData = MONTHS.map((m, i) => {
    const rec = records.find(r => r.month === i + 1);
    return { month: m, hours: rec?.hours_month || 0, total: rec?.hours_total_machine || 0 };
  });

  const intervals = equipment.maintenance_intervals || [];
  const totalHours = equipment.total_hours || 0;

  return (
    <div className="p-6 md:p-8 page-enter">
      {/* Header */}
      <div className="flex items-start gap-3 md:gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 mt-1 flex-shrink-0">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
              {equipment.name}
            </h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[equipment.status]}`}>
              {STATUS_LABELS[equipment.status]}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{equipment.code} · {equipment.location}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium px-2.5 md:px-3 py-2 rounded-lg"
            title="Editar equipo"
            data-testid="edit-equipment-detail-btn"
          >
            <Pencil size={15} /> <span className="hidden lg:inline">Editar</span>
          </button>
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowDelete(true)}
              className="flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium px-2.5 md:px-3 py-2 rounded-lg"
              title="Eliminar equipo"
              data-testid="delete-equipment-detail-btn"
            >
              <Trash2 size={15} /> <span className="hidden lg:inline">Eliminar</span>
            </button>
          )}
          <button
            onClick={() => setShowHourometer(true)}
            className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-2.5 md:px-4 py-2 rounded-lg"
            title="Registrar horas"
            data-testid="add-hourometer-btn"
          >
            <Plus size={15} /> <span className="hidden sm:inline">Registrar Horas</span>
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Horas Totales', value: `${totalHours.toLocaleString('es-ES')} h`, color: '#16a34a' },
          { label: 'Tipo', value: equipment.type === 'mobile' ? 'Móvil' : 'Fija', color: '#3b82f6' },
          { label: 'Sección', value: equipment.section || equipment.category || '—', color: '#8b5cf6' },
          { label: 'Registros Año', value: `${records.length} / 12`, color: '#f59e0b' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500">{stat.label}</p>
            <p className="text-lg font-bold mt-1" style={{ fontFamily: 'Barlow, sans-serif', color: stat.color }}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Description */}
      {equipment.description && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Descripción</p>
          <p className="text-sm text-gray-700">{equipment.description}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { key: 'horometro', label: 'Horómetro', icon: Clock },
          { key: 'mantenimiento', label: 'Plan Mantenimiento', icon: Wrench },
          { key: 'ordenes', label: `OTs (${workOrders.length})`, icon: AlertTriangle },
          { key: 'historial', label: `Historial (${activityLog.length})`, icon: History },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                ${activeTab === tab.key ? 'border-[#16a34a] text-[#16a34a]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'horometro' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
            >
              {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="text-xs text-gray-500">{records.length} registros encontrados</span>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 size={16} className="text-[#16a34a]" />
              <h3 className="text-sm font-semibold text-gray-900">Horas por Mes - {year}</h3>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`${v} h`, 'Horas/mes']} />
                <Bar dataKey="hours" fill="#16a34a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Records Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Mes</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">H/Mes</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">H Acum.</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">H Total</th>
                  {equipment.type === 'mobile' && <>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">KM/Mes</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Combustible</th>
                  </>}
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Ac. Hidráulico</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Observaciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">Sin registros para este año</td></tr>
                ) : records.map(rec => (
                  <tr key={rec.id} className="table-row-hover" data-testid={`record-row-${rec.id}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{MONTHS[rec.month - 1]} {rec.year}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{rec.hours_month || 0}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{rec.hours_accumulated_annual || 0}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-900">{rec.hours_total_machine || 0}</td>
                    {equipment.type === 'mobile' && <>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">{rec.km_month ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">{rec.fuel != null ? `${rec.fuel} L` : '—'}</td>
                    </>}
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{rec.oil_hydraulic != null ? `${rec.oil_hydraulic} L` : '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate">{rec.observations || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'mantenimiento' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Intervalo</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Último</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Próximo</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Horas Restantes</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Descripción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {intervals.map((interval, i) => {
                const last = Math.floor(totalHours / interval.hours) * interval.hours;
                const next = last + interval.hours;
                const remaining = next - totalHours;
                const isUrgent = remaining <= 50;
                const isOverdue = remaining <= 0;
                return (
                  <tr key={i} className={`table-row-hover ${isOverdue ? 'bg-red-50' : isUrgent ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{interval.type}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-600">{interval.hours} h</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-600">{last} h</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-600">{next} h</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-mono font-semibold text-sm ${isOverdue ? 'text-red-600' : isUrgent ? 'text-yellow-600' : 'text-gray-700'}`}>
                        {isOverdue ? 'VENCIDO' : `${remaining.toFixed(0)} h`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{interval.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'ordenes' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {workOrders.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">Sin órdenes de trabajo</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Código</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Título</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Asignado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {workOrders.map(wo => (
                  <tr key={wo.id} className="table-row-hover">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{wo.code}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{wo.title}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wo.type === 'corrective' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        {wo.type === 'corrective' ? 'Correctivo' : 'Preventivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        wo.status === 'completed' ? 'bg-green-100 text-green-700' :
                        wo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {wo.status === 'completed' ? 'Completada' : wo.status === 'in_progress' ? 'En curso' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{wo.assigned_to || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {activityLog.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">Sin actividad registrada para este equipo</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Acción</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Detalle</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activityLog.map((entry, i) => (
                  <tr key={entry.id || i} className="table-row-hover" data-testid={`activity-row-${i}`}>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(entry.timestamp).toLocaleString('es-ES', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">{entry.action}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 max-w-md">{entry.detail || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{entry.by_user || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showHourometer && (
        <HourometerModal equipment={equipment} onClose={() => setShowHourometer(false)} onSaved={load} />
      )}
      {showEdit && (
        <EquipmentModal type={equipment.type} equipment={equipment} onClose={() => setShowEdit(false)} onSaved={load} />
      )}
      {showDelete && (
        <DeleteEquipmentModal
          equipment={equipment}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate(equipment.type === 'mobile' ? '/maquinaria-movil' : '/maquinaria-fija')}
        />
      )}
    </div>
  );
}
